import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { healthDevicesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Watch,
  Heart,
  Drop,
  Lightning,
  Moon,
  Footprints,
  Thermometer,
  Plus,
  CheckCircle,
  SpinnerGap,
  Link as LinkIcon,
  LinkBreak,
  ArrowRight,
  Scales,
  Pulse,
  Bluetooth
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const providerIcons = {
  dexcom: <Drop size={24} className="text-green-600" />,
  freestyle_libre: <Drop size={24} className="text-blue-600" />,
  fitbit: <Watch size={24} className="text-pink-600" />,
  garmin: <Watch size={24} className="text-blue-700" />,
  apple_health: <Heart size={24} className="text-red-500" />,
  google_fit: <Pulse size={24} className="text-green-500" />,
  oura: <Moon size={24} className="text-purple-600" />,
  whoop: <Lightning size={24} className="text-yellow-600" />,
  withings: <Scales size={24} className="text-teal-600" />,
  omron: <Heart size={24} className="text-red-600" />,
  samsung_health: <Watch size={24} className="text-blue-500" />,
  polar: <Heart size={24} className="text-red-500" />,
  eight_sleep: <Moon size={24} className="text-indigo-600" />,
  manual: <Plus size={24} className="text-slate-500" />
};

const categoryColors = {
  cgm: 'bg-green-100 text-green-800',
  fitness: 'bg-blue-100 text-blue-800',
  aggregator: 'bg-purple-100 text-purple-800',
  sleep: 'bg-indigo-100 text-indigo-800',
  recovery: 'bg-yellow-100 text-yellow-800',
  health: 'bg-teal-100 text-teal-800',
  blood_pressure: 'bg-red-100 text-red-800',
  manual: 'bg-slate-100 text-slate-800'
};

export default function PatientHealthDevicesPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState({});
  const [connections, setConnections] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('devices');
  
  // Manual entry dialog
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualEntry, setManualEntry] = useState({ data_type: 'blood_pressure_systolic', value: '', secondary_value: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) toast.success('Device connected. Your readings will appear as they sync.');
    if (params.get('connect_failed')) toast.error('The device connection was not completed.');
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, connectionsRes, configRes, dataRes] = await Promise.all([
        healthDevicesAPI.getProviders(),
        healthDevicesAPI.getConnections(),
        healthDevicesAPI.getConfig(),
        healthDevicesAPI.getData({ days: 7 })
      ]);
      setProviders(providersRes.data.providers || {});
      setConnections(connectionsRes.data.connections || []);
      setConfig(configRes.data);
      setHealthData(dataRes.data.data || []);
    } catch (error) {
      console.error('Error loading health devices data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectDevice = async (provider) => {
    try {
      setSubmitting(true);
      
      if (provider === 'dexcom') {
        const res = await healthDevicesAPI.connectDexcom();
        window.location.href = res.data.auth_url;
      } else {
        const res = await healthDevicesAPI.connectTerra(provider);
        if (res.data.widget_url) {
          window.open(res.data.widget_url, '_blank', 'noopener,noreferrer');
          toast.success('Finish connecting in the new tab, then come back here');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start connection');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncDexcom = async () => {
    try {
      setSubmitting(true);
      const res = await healthDevicesAPI.syncDexcom();
      toast.success(`${res.data.readings_imported} new glucose readings`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dexcom sync failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${providers[provider]?.name}?`)) return;
    
    try {
      await healthDevicesAPI.disconnect(provider);
      toast.success('Device disconnected');
      loadData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleManualEntry = async () => {
    if (!manualEntry.value) {
      toast.error('Please enter a value');
      return;
    }
    
    try {
      setSubmitting(true);
      await healthDevicesAPI.addManualEntry({
        data_type: manualEntry.data_type,
        value: parseFloat(manualEntry.value),
        secondary_value: manualEntry.secondary_value ? parseFloat(manualEntry.secondary_value) : null,
        notes: manualEntry.notes
      });
      toast.success('Vital recorded successfully');
      setShowManualDialog(false);
      setManualEntry({ data_type: 'blood_pressure_systolic', value: '', secondary_value: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to save vital');
    } finally {
      setSubmitting(false);
    }
  };

  const isConnected = (provider) => connections.some(c => c.provider === provider);

  const isAvailable = (key) => {
    const integration = providers[key]?.integration;
    return Boolean(config?.[integration]?.configured);
  };

  const ConnectAction = ({ providerKey }) => {
    if (isConnected(providerKey)) {
      return (
        <div className="flex gap-2">
          <Badge className="bg-green-100 text-green-800" data-testid={`device-connected-${providerKey}`}>Connected</Badge>
          {providerKey === 'dexcom' && (
            <Button size="sm" variant="outline" onClick={handleSyncDexcom} disabled={submitting} data-testid="device-sync-dexcom">
              Sync now
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDisconnect(providerKey)} data-testid={`device-disconnect-${providerKey}`}>
            <LinkBreak size={14} className="mr-1" /> Disconnect
          </Button>
        </div>
      );
    }
    if (!isAvailable(providerKey)) {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid={`device-coming-soon-${providerKey}`}>
          Coming soon
        </Badge>
      );
    }
    return (
      <Button size="sm" onClick={() => handleConnectDevice(providerKey)} disabled={submitting} data-testid={`device-connect-${providerKey}`}>
        <LinkIcon size={14} className="mr-1" /> Connect
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Watch size={28} weight="duotone" className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Connected Health Devices
            </h1>
            <p className="text-slate-500 text-sm">Sync your wearables and health devices</p>
            {!config?.terra?.configured && !config?.dexcom?.configured && !config?.sahha?.configured && (
              <p className="text-amber-700 text-xs mt-1" data-testid="devices-coming-soon-note">
                Device syncing is coming soon. You can add your readings with Manual Entry now.
              </p>
            )}
          </div>
        </div>
        <Button onClick={() => setShowManualDialog(true)} variant="outline" data-testid="manual-entry-open-btn">
          <Plus size={18} className="mr-2" />
          Manual Entry
        </Button>
      </div>

      {/* Connected Devices Summary */}
      {connections.length > 0 && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle size={24} className="text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800">
                    {connections.length} Device{connections.length > 1 ? 's' : ''} Connected
                  </h3>
                  <p className="text-sm text-green-700">
                    {connections.map(c => providers[c.provider]?.name || c.provider).join(', ')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'devices' ? 'default' : 'outline'}
          onClick={() => setActiveTab('devices')}
          data-testid="tab-connect-devices"
          className={activeTab === 'devices' ? 'bg-teal-600' : ''}
        >
          <Bluetooth size={18} className="mr-2" />
          Connect Devices
        </Button>
        <Button
          variant={activeTab === 'data' ? 'default' : 'outline'}
          onClick={() => setActiveTab('data')}
          data-testid="tab-health-data"
          className={activeTab === 'data' ? 'bg-teal-600' : ''}
        >
          <Pulse size={18} className="mr-2" />
          My Health Data
        </Button>
      </div>

      {/* Connect Devices Tab */}
      {activeTab === 'devices' && (
        <div className="space-y-6">
          {/* CGM Devices */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Glucose Monitors (CGM)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(providers).filter(([_, p]) => p.category === 'cgm').map(([key, provider]) => (
                <Card key={key} className={`transition-all ${isConnected(key) ? 'border-green-300 bg-green-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {providerIcons[key]}
                        <div>
                          <h4 className="font-semibold">{provider.name}</h4>
                          <p className="text-xs text-slate-500">{provider.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <ConnectAction providerKey={key} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Fitness Wearables */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Fitness Wearables</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(providers).filter(([_, p]) => p.category === 'fitness' || p.category === 'aggregator').map(([key, provider]) => (
                <Card key={key} className={`transition-all ${isConnected(key) ? 'border-green-300 bg-green-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {providerIcons[key]}
                        <div>
                          <h4 className="font-semibold">{provider.name}</h4>
                          <p className="text-xs text-slate-500">{provider.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {provider.data_types?.slice(0, 4).map(dt => (
                        <Badge key={dt} variant="outline" className="text-xs">{dt}</Badge>
                      ))}
                    </div>
                    <div className="mt-4">
                      <ConnectAction providerKey={key} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Sleep & Recovery */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Sleep & Recovery</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(providers).filter(([_, p]) => p.category === 'sleep' || p.category === 'recovery').map(([key, provider]) => (
                <Card key={key} className={`transition-all ${isConnected(key) ? 'border-green-300 bg-green-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      {providerIcons[key]}
                      <div>
                        <h4 className="font-semibold">{provider.name}</h4>
                        <p className="text-xs text-slate-500">{provider.description}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <ConnectAction providerKey={key} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Phone Sensor */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-3">No Wearable? Use Your Phone</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(providers).filter(([_, p]) => p.category === 'phone_sensor').map(([key, provider]) => (
                <Card key={key} className={`transition-all ${isConnected(key) ? 'border-green-300 bg-green-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <Pulse size={24} className="text-teal-600" />
                      <div>
                        <h4 className="font-semibold">{provider.name}</h4>
                        <p className="text-xs text-slate-500">{provider.description}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <ConnectAction providerKey={key} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Health Monitors */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Health Monitors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(providers).filter(([_, p]) => p.category === 'health' || p.category === 'blood_pressure').map(([key, provider]) => (
                <Card key={key} className={`transition-all ${isConnected(key) ? 'border-green-300 bg-green-50' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      {providerIcons[key]}
                      <div>
                        <h4 className="font-semibold">{provider.name}</h4>
                        <p className="text-xs text-slate-500">{provider.description}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <ConnectAction providerKey={key} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Health Data Tab */}
      {activeTab === 'data' && (
        <div className="space-y-4">
          {healthData.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Pulse size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No Health Data Yet</h3>
                <p className="text-slate-500 mt-1">Connect a device or add manual entries to see your health data</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {healthData.slice(0, 20).map(record => (
                <Card key={record.id} data-testid={`health-record-${record.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {record.data_type === 'glucose' && <Drop size={20} className="text-green-600" />}
                        {record.data_type === 'heart_rate' && <Heart size={20} className="text-red-500" />}
                        {record.data_type === 'steps' && <Footprints size={20} className="text-blue-500" />}
                        {record.data_type === 'weight' && <Scales size={20} className="text-teal-500" />}
                        {record.data_type === 'blood_pressure_systolic' && <Heart size={20} className="text-red-600" />}
                        {record.data_type === 'temperature' && <Thermometer size={20} className="text-orange-500" />}
                        <div>
                          <p className="font-medium capitalize">{record.data_type === 'blood_pressure_systolic' ? 'Blood pressure' : record.data_type.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(record.recorded_at).toLocaleString()} • {record.provider}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-800">
                          {record.value}{record.secondary_value ? `/${record.secondary_value}` : ''} 
                          <span className="text-sm font-normal text-slate-500 ml-1">{record.unit}</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Vital Entry</DialogTitle>
            <DialogDescription>Record a vital sign measurement</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Vital Type</Label>
              <select
                data-testid="manual-entry-type-select"
                value={manualEntry.data_type}
                onChange={(e) => setManualEntry({ ...manualEntry, data_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
              >
                <option value="blood_pressure_systolic">Blood Pressure</option>
                <option value="glucose">Blood Glucose</option>
                <option value="heart_rate">Heart Rate</option>
                <option value="weight">Weight</option>
                <option value="temperature">Temperature</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{manualEntry.data_type === 'blood_pressure_systolic' ? 'Systolic' : 'Value'}</Label>
                <Input
                  type="number"
                  value={manualEntry.value}
                  data-testid="manual-entry-value-input"
                  onChange={(e) => setManualEntry({ ...manualEntry, value: e.target.value })}
                  placeholder={manualEntry.data_type === 'blood_pressure_systolic' ? '120' : ''}
                />
              </div>
              {manualEntry.data_type === 'blood_pressure_systolic' && (
                <div>
                  <Label>Diastolic</Label>
                  <Input
                    type="number"
                    value={manualEntry.secondary_value}
                    data-testid="manual-entry-diastolic-input"
                    onChange={(e) => setManualEntry({ ...manualEntry, secondary_value: e.target.value })}
                    placeholder="80"
                  />
                </div>
              )}
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={manualEntry.notes}
                data-testid="manual-entry-notes-input"
                onChange={(e) => setManualEntry({ ...manualEntry, notes: e.target.value })}
                placeholder="Any notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualDialog(false)} data-testid="manual-entry-cancel-btn">Cancel</Button>
            <Button onClick={handleManualEntry} data-testid="manual-entry-save-btn" disabled={submitting} className="bg-teal-600 hover:bg-teal-700">
              {submitting ? <SpinnerGap size={18} className="animate-spin mr-2" /> : <Plus size={18} className="mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
